import { useCallback, useState } from 'react'

export interface UseWizardStateOptions {
  totalSteps: number
  initialStep?: number
}

export interface WizardState {
  currentIndex: number
  isFirst: boolean
  isLast: boolean
  goNext: () => void
  goPrev: () => void
  goTo: (index: number) => void
  reset: () => void
}

/**
 * Estado mínimo del wizard: índice activo + navegación. No conoce validación
 * (eso lo gobierna el componente que lo usa, llamando `goNext` después de
 * verificar el paso actual).
 */
export function useWizardState({ totalSteps, initialStep = 0 }: UseWizardStateOptions): WizardState {
  const [currentIndex, setCurrentIndex] = useState(initialStep)

  const goNext = useCallback(() => {
    setCurrentIndex((idx) => Math.min(idx + 1, totalSteps - 1))
  }, [totalSteps])

  const goPrev = useCallback(() => {
    setCurrentIndex((idx) => Math.max(idx - 1, 0))
  }, [])

  const goTo = useCallback(
    (index: number) => {
      if (index < 0 || index >= totalSteps) return
      setCurrentIndex(index)
    },
    [totalSteps],
  )

  const reset = useCallback(() => setCurrentIndex(initialStep), [initialStep])

  return {
    currentIndex,
    isFirst: currentIndex === 0,
    isLast: currentIndex === totalSteps - 1,
    goNext,
    goPrev,
    goTo,
    reset,
  }
}
